import React from 'react';
import './BookingInvoiceStyles.css';

const BookingInvoice = ({ booking, onClose }) => {
    // Map status values to display-friendly values and colors
    const statusMapping = {
        'PENDING': { display: 'Pending', class: 'status-pending' },
        'CONFIRMED': { display: 'Confirmed', class: 'status-confirmed' },
        'CANCELLED': { display: 'Cancelled', class: 'status-cancelled' },
        'COMPLETED': { display: 'Completed', class: 'status-completed' },
        'pending': { display: 'Pending', class: 'status-pending' },
        'confirmed': { display: 'Confirmed', class: 'status-confirmed' },
        'cancelled': { display: 'Cancelled', class: 'status-cancelled' },
        'completed': { display: 'Completed', class: 'status-completed' }
    };

    // Normalize the status for display
    const getStatusInfo = (status) => {
        status = (status || 'PENDING').toUpperCase();
        return statusMapping[status] || { display: status, class: 'status-pending' };
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not specified';
        try {
            const date = new Date(dateString);

            // Check if the date is valid
            if (isNaN(date.getTime())) {
                console.error('Invalid date value:', dateString);
                return 'Date unavailable';
            }

            return date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error, dateString);
            return 'Date unavailable';
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';

        // Handle HH:MM:SS format
        if (timeString.includes(':')) {
            // If it's already in HH:MM:SS format, convert to 12-hour format
            const timeParts = timeString.split(':');
            if (timeParts.length >= 2) {
                const hours = parseInt(timeParts[0]);
                const minutes = timeParts[1];
                const period = hours >= 12 ? 'PM' : 'AM';
                const hour12 = hours % 12 || 12;
                return `${hour12}:${minutes} ${period}`;
            }
        }

        return timeString;
    };

    // Generate a random invoice number if not provided
    const invoiceNumber = booking.invoiceNumber || `INV-${Math.floor(100000 + Math.random() * 900000)}`;
    const bookingDate = formatDate(booking.bookingDate || booking.booking_date || booking.createdAt || Date.now());

    // Normalize booking data for consistent access
    const eventName = booking.functionName ||
        (booking.function_id && booking.function_id.functionName) ||
        booking.selectedOccasion ||
        booking.occasion ||
        'Event Service';

    const eventPrice = booking.occasionPrice ||
        (booking.function_id && booking.function_id.functionPrice) || 0;

    const eventDate = formatDate(booking.bookingForDate || booking.date || booking.booking_for_date);

    // Format time display
    const startTimeFormatted = booking.startTime
        ? formatTime(booking.startTime)
        : (booking.start_time ? formatTime(booking.start_time) : '');

    const endTimeFormatted = booking.endTime
        ? formatTime(booking.endTime)
        : (booking.end_time ? formatTime(booking.end_time) : '');

    const eventTime = (startTimeFormatted && endTimeFormatted)
        ? `${startTimeFormatted} - ${endTimeFormatted}`
        : 'Not specified';

    const bookingId = booking.bookingId || booking.id || booking.bookingID || 'Not assigned';
    const totalAmount = booking.totalPaymentAmount || booking.totalAmount || booking.total || 0;

    // Get status info
    const status = booking.bookingStatus || booking.status || booking.booking_status || 'PENDING';
    const statusInfo = getStatusInfo(status);

    // Get customer info
    const customerName = booking.firstName && booking.lastName
        ? `${booking.firstName} ${booking.lastName}`
        : booking.customerName ||
        (booking.user_id && (booking.user_id.firstName || booking.user_id.name)) ||
        'Guest User';

    const customerEmail = booking.email ||
        booking.customerEmail ||
        (booking.user_id && booking.user_id.email) ||
        'Not provided';

    const customerPhone = booking.mobile ||
        booking.customerPhone ||
        (booking.user_id && booking.user_id.phoneNumber) ||
        'Not provided';

    // Handle decorations
    const hasDecorations = booking.decorations && booking.decorations.length > 0;

    // Handle subfunctions (services) and items
    const hasSubfunctions = booking.subfunction && booking.subfunction.length > 0;
    const hasItems = booking.items && booking.items.length > 0;

    return (
        <div className="invoice-container">
            <div className="invoice-card">
                <div className="invoice-header">
                    <h2>Booking Invoice</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="invoice-company">
                    <div className="company-info">
                        <h3>Waghchaure Lawns</h3>
                        <p>123 Garden Street, Pune, Maharashtra</p>
                        <p>Phone: +91 98765 43210</p>
                        <p>Email: info@waghchaurelawns.com</p>
                    </div>
                    <div className="invoice-meta">
                        <div className="invoice-id">
                            <h4>Invoice #</h4>
                            <p>{invoiceNumber}</p>
                        </div>
                        <div className="invoice-date">
                            <h4>Date</h4>
                            <p>{bookingDate}</p>
                        </div>
                    </div>
                </div>

                <div className="invoice-customer">
                    <h3>Customer Details</h3>
                    <p><strong>Name:</strong> {customerName}</p>
                    <p><strong>Email:</strong> {customerEmail}</p>
                    <p><strong>Phone:</strong> {customerPhone}</p>
                </div>

                <div className="invoice-event-details">
                    <h3>Event Details</h3>
                    <div className="detail-row">
                        <p><strong>Event Type:</strong> {eventName}</p>
                        <p><strong>Date:</strong> {eventDate}</p>
                    </div>
                    <div className="detail-row">
                        <p><strong>Time:</strong> {eventTime}</p>
                        <p><strong>Booking ID:</strong> {bookingId}</p>
                    </div>
                    <div className="detail-row">
                        <p><strong>Status:</strong> <span className={statusInfo.class}>{statusInfo.display}</span></p>
                        {booking.paymentMode && (
                            <p><strong>Payment Mode:</strong> {booking.paymentMode}</p>
                        )}
                    </div>
                </div>

                <div className="invoice-items">
                    <h3>Items</h3>
                    <table className="items-table">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th>Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Main event service */}
                            <tr>
                                <td>{eventName} (Event Package)</td>
                                <td>₹{Number(eventPrice).toLocaleString()}</td>
                            </tr>

                            {/* Subfunctions and their items */}
                            {hasSubfunctions && booking.subfunction.map((sub, index) => (
                                <React.Fragment key={`sub-${index}`}>
                                    {/* Subfunction row */}
                                    <tr className="subfunction-row">
                                        <td>{sub.subfunctionName || 'Additional Service'}</td>
                                        <td>₹{Number(sub.price || 0).toLocaleString()}</td>
                                    </tr>
                                    
                                    {/* Items related to this subfunction - if we can identify them */}
                                    {hasItems && booking.items
                                        .filter(item => item.subfunctionId === sub.id)
                                        .map((item, itemIdx) => (
                                            <tr key={`subitem-${itemIdx}`} className="item-row">
                                                <td className="item-name">— {item.name || 'Equipment Item'}</td>
                                                <td>Included</td>
                                            </tr>
                                        ))
                                    }
                                </React.Fragment>
                            ))}

                            {/* Decorations - if available */}
                            {hasDecorations && booking.decorations.map((deco, index) => (
                                <tr key={`deco-${index}`}>
                                    <td>{deco.name || 'Decoration Package'}</td>
                                    <td>₹{Number(deco.price || 0).toLocaleString()}</td>
                                </tr>
                            ))}

                            {/* Any remaining items that couldn't be associated with a subfunction */}
                            {hasItems && hasSubfunctions && booking.items
                                .filter(item => !booking.subfunction.some(sub => sub.id === item.subfunctionId))
                                .map((item, index) => (
                                    <tr key={`item-${index}`} className="item-row">
                                        <td>{item.name || 'Equipment Item'}</td>
                                        <td>Included</td>
                                    </tr>
                                ))
                            }
                            
                            {/* If we don't have subfunctions but have items */}
                            {hasItems && !hasSubfunctions && booking.items.map((item, index) => (
                                <tr key={`item-${index}`} className="item-row">
                                    <td>{item.name || 'Equipment Item'}</td>
                                    <td>Included</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="invoice-summary">
                    <div className="summary-row">
                        <span>Subtotal</span>
                        <span>₹{Number(totalAmount).toLocaleString()}</span>
                    </div>
                    {booking.tax && (
                        <div className="summary-row">
                            <span>Tax (18% GST)</span>
                            <span>₹{Number(booking.tax || 0).toLocaleString()}</span>
                        </div>
                    )}
                    <div className="summary-row total">
                        <span>Total</span>
                        <span>₹{Number(totalAmount).toLocaleString()}</span>
                    </div>
                </div>

                <div className="invoice-footer">
                    <p>Thank you for choosing Waghchaure Lawns for your event!</p>
                    <div className="invoice-actions">
                        <button className="btn print-btn" onClick={() => window.print()}>
                            Print Invoice
                        </button>
                        <button className="btn close-btn" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingInvoice; 